import { BsaDataStaticsGisRes, getBsaDataStaticsGis } from '@/api/bsaMap';
import { Breadcrumb, useBreadcrumbRoutes } from '@maxtropy/components';
import { Spin } from 'antd';
import { useSize } from 'ahooks';
import classNames from 'classnames';
import classNamesBind from 'classnames/bind';
import { useCallback, useEffect, useRef, useState } from 'react';
import LeftInfo from './LeftInfo';
import RightInfo from './RightInfo';
import TopSummary from './TopSummary';
import styles from './index.module.scss';
import { clusterStations, createStationIcon, getClusterStyle, StationPoint, toStationPoints } from './stationCluster';
import { getConfiguredTiandituToken, loadTianditu } from './tiandituLoader';
import { flexible } from './utils';

const cx = classNamesBind.bind(styles);
const DEFAULT_CENTER = [104.195397, 35.86166] as const;

export interface DetailInfoProps {
  id: number | undefined;
  showPosition: boolean;
  showDetails: boolean;
}

const MapStation = () => {
  const routesContext = useBreadcrumbRoutes();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<BsaDataStaticsGisRes>();
  const [detailInfo, setDetailInfo] = useState<DetailInfoProps>();
  const pageRef = useRef<HTMLDivElement>();
  const contentRef = useRef<HTMLDivElement>();
  const mapRef = useRef<any>();
  const tiandituRef = useRef<any>();
  const overlaysRef = useRef<any[]>([]);
  const pointsRef = useRef<StationPoint[]>([]);
  const selectedIdRef = useRef<number>();
  const renderMarkersRef = useRef<() => void>();
  const contentSize = useSize(contentRef);

  useEffect(() => {
    getBsaDataStaticsGis()
      .then(setData)
      .catch(() => setError('站点数据加载失败，请稍后重试'));
  }, []);

  const clearOverlays = useCallback(() => {
    const map = mapRef.current;
    overlaysRef.current.forEach(overlay => map?.removeOverLay?.(overlay));
    overlaysRef.current = [];
  }, []);

  const renderMarkers = useCallback(() => {
    const map = mapRef.current;
    const T = tiandituRef.current;
    if (!map || !T) return;

    clearOverlays();
    const zoom = Number(map.getZoom?.() ?? 5);
    const groups = clusterStations(pointsRef.current, zoom);

    groups.forEach(group => {
      const isCluster = group.points.length > 1;
      const point = group.points[0];

      if (isCluster) {
        const clusterStyle = getClusterStyle(group.points.length);
        const label = new T.Label({
          text: `<div class="maker-cluster ${clusterStyle.className}"><span>${group.points.length}</span></div>`,
          position: new T.LngLat(group.longitude, group.latitude),
          offset: new T.Point(-clusterStyle.size / 2, -clusterStyle.size / 2),
        });
        label.setBorderLine?.(0);
        label.setBackgroundColor?.('transparent');
        label.setBorderColor?.('transparent');
        label.addEventListener('click', () => {
          const nextZoom = Math.min(15, Math.max(8, zoom + 2));
          map.centerAndZoom(new T.LngLat(group.longitude, group.latitude), nextZoom);
        });
        map.addOverLay(label);
        label.getElement?.()?.classList?.add('tdt-marker-cluster');
        overlaysRef.current.push(label);
        return;
      }

      const width = 28;
      const height = 38;
      const icon = new T.Icon({
        iconUrl: createStationIcon(point.id === selectedIdRef.current),
        iconSize: new T.Point(width, height),
        iconAnchor: new T.Point(width / 2, height),
      });
      const marker = new T.Marker(new T.LngLat(group.longitude, group.latitude), { icon });

      marker.addEventListener('click', () => {
        selectedIdRef.current = point.id;
        setDetailInfo({ id: point.id, showPosition: true, showDetails: true });
        renderMarkersRef.current?.();
      });

      map.addOverLay(marker);
      overlaysRef.current.push(marker);
    });
  }, [clearOverlays]);

  useEffect(() => {
    renderMarkersRef.current = renderMarkers;
  }, [renderMarkers]);

  useEffect(() => {
    if (!data) return;
    const token = getConfiguredTiandituToken();
    if (!token) {
      setError('缺少天地图 Key，请在 dynamic/config.js 中配置 window.TIANDITU_TOKEN');
      return;
    }

    let disposed = false;
    let loadingTimer: number | undefined;
    const handleTilesLoaded = () => {
      window.clearTimeout(loadingTimer);
      if (!disposed) setLoading(false);
    };
    const handleZoomEnd = () => renderMarkersRef.current?.();

    setLoading(true);
    setError('');
    pointsRef.current = toStationPoints(data.list);

    loadTianditu(token)
      .then(T => {
        if (disposed) return;
        tiandituRef.current = T;
        const map = new T.Map('tdt-map-container', {
          minZoom: 1,
          maxZoom: 15,
          projection: 'EPSG:900913',
        });
        mapRef.current = map;
        map.centerAndZoom(new T.LngLat(DEFAULT_CENTER[0], DEFAULT_CENTER[1]), 5);
        if (window.TMAP_SATELLITE_MAP) {
          map.setMapType(window.TMAP_SATELLITE_MAP);
        }
        map.addEventListener('tilesloaded', handleTilesLoaded);
        map.addEventListener('zoomend', handleZoomEnd);
        renderMarkersRef.current?.();
        loadingTimer = window.setTimeout(handleTilesLoaded, 3000);
      })
      .catch(reason => {
        if (disposed) return;
        setLoading(false);
        setError(reason instanceof Error ? reason.message : '天地图加载失败');
      });

    return () => {
      disposed = true;
      window.clearTimeout(loadingTimer);
      const map = mapRef.current;
      map?.removeEventListener?.('tilesloaded', handleTilesLoaded);
      map?.removeEventListener?.('zoomend', handleZoomEnd);
      clearOverlays();
      mapRef.current = undefined;
      tiandituRef.current = undefined;
    };
  }, [clearOverlays, data]);

  useEffect(() => {
    if (!detailInfo?.showPosition) return;
    const map = mapRef.current;
    const T = tiandituRef.current;
    if (!map || !T) return;

    selectedIdRef.current = detailInfo.id;
    renderMarkersRef.current?.();
    const point = pointsRef.current.find(item => item.id === detailInfo.id);
    if (point) {
      map.centerAndZoom(new T.LngLat(point.longitude, point.latitude), Math.max(8, Number(map.getZoom?.() ?? 8)));
    } else {
      map.setZoom?.(5);
    }
  }, [detailInfo]);

  useEffect(() => {
    const handleResize = () => {
      flexible(pageRef.current);
      mapRef.current?.checkResize?.();
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className="page"
      ref={ref => {
        if (ref) pageRef.current = ref;
      }}
      style={{ width: '100%', height: '100%' }}
    >
      <div className="page__header">
        <Breadcrumb routes={routesContext?.routes ?? []} />
      </div>
      <div
        ref={ref => {
          if (ref) contentRef.current = ref;
        }}
      >
        <div className={cx('border-wrapper')}>
          <div className={cx('border-wrapper-inner')}>
            <div className={classNames(cx('pageContent'), styles.overlayLayer)}>
              <div className={styles.LeftWapper}>
                <LeftInfo
                  divHeight={contentSize?.height}
                  detailInfo={detailInfo}
                  changeIsDetail={(id, showPosition, showDetails) => setDetailInfo({ id, showPosition, showDetails })}
                  data={data}
                />
              </div>
              <div className={styles.TopWapper}>
                <TopSummary data={data} />
              </div>
              <div className={styles.RightWapper}>
                <RightInfo data={data} divHeight={contentSize?.height} />
              </div>
            </div>
            <Spin spinning={loading}>
              <div className={styles.mapContainer} style={{ height: contentSize ? contentSize.height - 40 : 'unset' }}>
                <div id="tdt-map-container" className={styles.mapContainer} />
                {error ? <div className={styles.mapError}>{error}</div> : null}
              </div>
            </Spin>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapStation;
